import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  KeyIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CalendarDaysIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui';
import { MetricCard } from '../../components/ui/MetricCard';
import { Badge } from '../../components/ui/Badge';
import { statisticsService } from '../../services/statisticsService';
import { licenseService } from '../../services/licenseService';
import { formatDate } from '../../utils/formatters';
import { ROUTES } from '../../constants';

/** La API devuelve `data` como array; algunos clientes esperaban `results`. */
const normalizeLicenseList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload?.results && Array.isArray(payload.results)) return payload.results;
  return [];
};

const EXPIRING_WINDOW_DAYS = 30;
const RECENT_VALIDATIONS_LIMIT = 10;

const getValidationResultLabel = (result) => {
  const labels = {
    success: 'Exitosa',
    failed: 'Fallida',
    expired: 'Expirada',
    suspended: 'Suspendida',
    limit_exceeded: 'Límite excedido',
  };
  return labels[result] || result || '—';
};

const getValidationResultBadgeVariant = (result) => {
  switch (result) {
    case 'success':
      return 'success';
    case 'failed':
      return 'danger';
    case 'expired':
    case 'suspended':
      return 'warning';
    case 'limit_exceeded':
      return 'danger';
    default:
      return 'secondary';
  }
};

const getValidationEventTypeLabel = (eventType) => {
  const labels = {
    new_installation: 'Nueva instalación',
    revalidation: 'Revalidación',
    installation_id_mac_conflict: 'Conflicto ID / MAC',
    heartbeat: 'Heartbeat',
    legacy: 'Histórico',
  };
  return labels[eventType] || eventType || '—';
};

/**
 * El backend aplica el rango por fecha de calendario en TIME_ZONE (param
 * `expiring_within_days`), así no falla el parseo de ISO en query strings ni se
 * excluye "vence hoy" por usar la hora actual como límite inferior.
 */
const expiringLicensesQueryParams = () => ({
  expiring_within_days: EXPIRING_WINDOW_DAYS,
  ordering: 'expiry_date',
});

/** Días de calendario locales hasta el vencimiento (0 = hoy), alineado con lo que ve el usuario. */
const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const end = new Date(isoDate);
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((endDay - today) / 86400000);
};

export const DashboardPage = () => {
  // Obtener estadísticas del sistema
  const { data: statisticsData, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['dashboard-statistics'],
    queryFn: () => statisticsService.getPublicStatistics(),
    refetchInterval: 30000, // Refrescar cada 30 segundos
  });

  const { data: recentLicensesData, isLoading: recentLoading } = useQuery({
    queryKey: ['dashboard-recent-licenses'],
    queryFn: () => licenseService.getLicenses({ ordering: '-created_at' }),
    refetchInterval: 60000,
  });

  const {
    data: expiringLicensesData,
    isLoading: expiringLoading,
    isError: expiringError,
  } = useQuery({
    queryKey: ['dashboard-expiring-licenses', EXPIRING_WINDOW_DAYS],
    queryFn: () => licenseService.getLicenses(expiringLicensesQueryParams()),
    refetchInterval: 60000,
  });

  const {
    data: recentValidationsData,
    isLoading: validationsLoading,
    isError: validationsError,
  } = useQuery({
    queryKey: ['dashboard-recent-validations', RECENT_VALIDATIONS_LIMIT],
    queryFn: () =>
      licenseService.getValidationLogs({
        limit: RECENT_VALIDATIONS_LIMIT,
      }),
    refetchInterval: 60000,
  });

  // Procesar datos de estadísticas
  const metrics = statisticsData?.statistics ? {
    activeLicenses: statisticsData.statistics.active_licenses || 0,
    totalClients: statisticsData.statistics.total_clients || 0,
    totalLicenses: statisticsData.statistics.total_licenses || 0,
    trialLicenses: statisticsData.statistics.trial_licenses || 0,
    totalSoftware: statisticsData.statistics.total_software || 0,
  } : {
    activeLicenses: 0,
    totalClients: 0,
    totalLicenses: 0,
    trialLicenses: 0,
    totalSoftware: 0,
  };

  const recentLicenses = normalizeLicenseList(recentLicensesData).slice(0, 5);
  const expiringLicenses = normalizeLicenseList(expiringLicensesData).slice(0, 8);
  const recentValidations = normalizeLicenseList(recentValidationsData).slice(
    0,
    RECENT_VALIDATIONS_LIMIT
  );

  const urgencyBadgeVariant = (days) => {
    if (days == null) return 'secondary';
    if (days <= 7) return 'danger';
    if (days <= EXPIRING_WINDOW_DAYS) return 'warning';
    return 'secondary';
  };

  // Función para obtener el color del badge según el estado
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trial':
        return 'warning';
      case 'expired':
        return 'danger';
      case 'suspended':
        return 'danger';
      case 'cancelled':
        return 'danger';
      default:
        return 'default';
    }
  };

  // Función para obtener el texto del estado
  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Activa';
      case 'trial':
        return 'Trial';
      case 'expired':
        return 'Expirada';
      case 'suspended':
        return 'Suspendida';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status;
    }
  };

  // Loading state
  if (statsLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Resumen general del sistema de licencias</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (statsError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Resumen general del sistema de licencias</p>
        </div>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3 text-danger-600">
              <ExclamationTriangleIcon className="h-6 w-6" />
              <div>
                <h3 className="font-medium">Error al cargar estadísticas</h3>
                <p className="text-sm text-gray-600">
                  No se pudieron cargar los datos del dashboard. Por favor, intenta nuevamente.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Resumen general del sistema de licencias</p>
        {statisticsData?.statistics?.last_updated && (
          <p className="text-sm text-gray-500 mt-1">
            Última actualización: {formatDate(statisticsData.statistics.last_updated, 'datetime')}
          </p>
        )}
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Licencias Activas"
          value={metrics.activeLicenses.toLocaleString()}
          icon={<KeyIcon className="h-8 w-8" />}
        />
        
        <MetricCard
          title="Total Clientes"
          value={metrics.totalClients.toLocaleString()}
          icon={<UsersIcon className="h-8 w-8" />}
        />
        
        <MetricCard
          title="Licencias Trial"
          value={metrics.trialLicenses.toLocaleString()}
          icon={<ComputerDesktopIcon className="h-8 w-8" />}
        />
        
        <MetricCard
          title="Software Registrado"
          value={metrics.totalSoftware.toLocaleString()}
          icon={<CurrencyDollarIcon className="h-8 w-8" />}
        />
      </div>

      {/* System Status and Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Status */}
        <Card>
          <CardHeader>
            <CardTitle>Estado del Sistema</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Base de Datos</span>
                <div className="flex items-center space-x-2">
                  <div className="h-2 w-2 bg-success-500 rounded-full"></div>
                  <span className="text-sm text-success-600">Conectada</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">API de Licencias</span>
                <div className="flex items-center space-x-2">
                  <div className="h-2 w-2 bg-success-500 rounded-full"></div>
                  <span className="text-sm text-success-600">Operativa</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Servicio de Emails</span>
                <div className="flex items-center space-x-2">
                  <div className="h-2 w-2 bg-success-500 rounded-full"></div>
                  <span className="text-sm text-success-600">Activo</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Acciones Rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <button className="p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-2">
                  <KeyIcon className="h-5 w-5 text-primary-600" />
                  <span className="text-sm font-medium">Nueva Licencia</span>
                </div>
              </button>
              <button className="p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-2">
                  <UsersIcon className="h-5 w-5 text-primary-600" />
                  <span className="text-sm font-medium">Agregar Cliente</span>
                </div>
              </button>
              <button className="p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-2">
                  <ComputerDesktopIcon className="h-5 w-5 text-primary-600" />
                  <span className="text-sm font-medium">Registrar Software</span>
                </div>
              </button>
              <button className="p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-2">
                  <CurrencyDollarIcon className="h-5 w-5 text-primary-600" />
                  <span className="text-sm font-medium">Ver Reportes</span>
                </div>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* License Types Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Distribución de Licencias</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Licencias Activas</span>
                <span className="text-sm font-medium">{metrics.activeLicenses}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Licencias Trial</span>
                <span className="text-sm font-medium">{metrics.trialLicenses}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total de Licencias</span>
                <span className="text-sm font-medium">{metrics.totalLicenses}</span>
              </div>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 text-center">
                  Gráfico interactivo disponible próximamente
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Resumen del Sistema</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Clientes Activos</span>
                <span className="text-sm font-medium">{metrics.totalClients}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Software Registrado</span>
                <span className="text-sm font-medium">{metrics.totalSoftware}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Tasa de Conversión</span>
                <span className="text-sm font-medium">
                  {metrics.totalLicenses > 0 ? 
                    ((metrics.activeLicenses / metrics.totalLicenses) * 100).toFixed(1) : 0}%
                </span>
              </div>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 text-center">
                  Métricas avanzadas disponibles próximamente
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Licencias recientes y próximas a expirar */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="border border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3">
            <CardTitle className="text-base font-semibold text-gray-900">
              Licencias recientes
            </CardTitle>
            <Link
              to={ROUTES.LICENSES}
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Ver todas
            </Link>
          </CardHeader>
          <CardContent className="pt-4">
            {recentLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-3/4" />
                  </div>
                ))}
              </div>
            ) : recentLicenses.length > 0 ? (
              <div className="overflow-x-auto -mx-1">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Clave
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Cliente
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Software
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Estado
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Emisión
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {recentLicenses.map((license) => (
                      <tr key={license.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 font-mono text-gray-900 whitespace-nowrap">
                          {license.license_key}
                        </td>
                        <td className="px-3 py-2.5 text-gray-900 whitespace-nowrap">
                          {license.client_name || license.client?.name || '—'}
                        </td>
                        <td className="px-3 py-2.5 text-gray-900 whitespace-nowrap">
                          {license.software_name || license.software?.name || '—'}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <Badge
                            variant={getStatusBadgeVariant(license.status)}
                            className="text-xs"
                          >
                            {getStatusText(license.status)}
                          </Badge>
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">
                          {license.issued_date
                            ? formatDate(license.issued_date, 'short')
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-10 px-4">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <CheckCircleIcon className="h-6 w-6 text-gray-500" aria-hidden />
                </div>
                <h3 className="mt-3 text-sm font-semibold text-gray-900">No hay licencias</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Comienza creando tu primera licencia.
                </p>
                <Link
                  to={ROUTES.LICENSES}
                  className="mt-4 inline-block text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  Ir a licencias
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3">
            <CardTitle className="text-base font-semibold text-gray-900">
              Próximas a expirar
            </CardTitle>
            <span className="text-xs text-gray-500">
              Próximos {EXPIRING_WINDOW_DAYS} días
            </span>
          </CardHeader>
          <CardContent className="pt-4">
            {expiringLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-3/4" />
                  </div>
                ))}
              </div>
            ) : expiringError ? (
              <div className="text-center py-8 px-4">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-warning-500" />
                <p className="mt-2 text-sm font-medium text-gray-900">
                  No se pudieron cargar las licencias próximas a vencer
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Comprueba la conexión y que el backend esté actualizado (parámetro{' '}
                  <code className="font-mono bg-gray-100 px-1 rounded">expiring_within_days</code>
                  ).
                </p>
              </div>
            ) : expiringLicenses.length > 0 ? (
              <div className="overflow-x-auto -mx-1">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Clave
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Cliente
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Vencimiento
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Restante
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {expiringLicenses.map((license) => {
                      const d = daysUntil(license.expiry_date);
                      return (
                        <tr key={license.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2.5 font-mono text-gray-900 whitespace-nowrap">
                            {license.license_key}
                          </td>
                          <td className="px-3 py-2.5 text-gray-900 whitespace-nowrap">
                            {license.client_name || license.client?.name || '—'}
                          </td>
                          <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                            {license.expiry_date
                              ? formatDate(license.expiry_date, 'short')
                              : '—'}
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            {d != null ? (
                              <Badge
                                variant={urgencyBadgeVariant(d)}
                                className="text-xs"
                              >
                                {d === 0
                                  ? 'Hoy'
                                  : d === 1
                                    ? '1 día'
                                    : `${d} días`}
                              </Badge>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-10 px-4">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <CalendarDaysIcon className="h-6 w-6 text-gray-500" aria-hidden />
                </div>
                <h3 className="mt-3 text-sm font-semibold text-gray-900">
                  Sin vencimientos próximos
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  No hay licencias activas que venzan en los próximos {EXPIRING_WINDOW_DAYS}{' '}
                  días, o no tienen fecha de expiración.
                </p>
                <Link
                  to={ROUTES.LICENSES}
                  className="mt-4 inline-block text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  Ver licencias
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Últimas licencias validadas */}
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3">
          <CardTitle className="text-base font-semibold text-gray-900">
            Últimas licencias validadas
          </CardTitle>
          <span className="text-xs text-gray-500">
            Últimas {RECENT_VALIDATIONS_LIMIT} validaciones
          </span>
        </CardHeader>
        <CardContent className="pt-4">
          {validationsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : validationsError ? (
            <div className="text-center py-8 px-4">
              <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-warning-500" />
              <p className="mt-2 text-sm font-medium text-gray-900">
                No se pudieron cargar las validaciones recientes
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Comprueba la conexión y que el backend exponga{' '}
                <code className="font-mono bg-gray-100 px-1 rounded">
                  /licenses/api/validation-logs/
                </code>
                .
              </p>
            </div>
          ) : recentValidations.length > 0 ? (
            <div className="overflow-x-auto -mx-1">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Fecha
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Clave
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Cliente
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Software
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Evento
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Resultado
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Equipo
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {recentValidations.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                        {log.validation_date
                          ? formatDate(log.validation_date, 'datetime')
                          : '—'}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-gray-900 whitespace-nowrap">
                        {log.license_key || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-gray-900 whitespace-nowrap">
                        {log.client_name || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-gray-900 whitespace-nowrap">
                        {log.software_name || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                        {getValidationEventTypeLabel(log.event_type)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <Badge
                          variant={getValidationResultBadgeVariant(
                            log.validation_result
                          )}
                          className="text-xs"
                        >
                          {getValidationResultLabel(log.validation_result)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span>{log.machine_name || '—'}</span>
                          {log.mac_address ? (
                            <span className="font-mono text-xs text-gray-500">
                              {log.mac_address}
                            </span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-10 px-4">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                <ShieldCheckIcon className="h-6 w-6 text-gray-500" aria-hidden />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-gray-900">
                Sin validaciones registradas
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Cuando los clientes validen licencias, aparecerán aquí.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

